import { isCareIndustryPageKey } from './careIndustryLabels.js';
import { createCareIndustryPages } from './careIndustryPages.js';
import { isRetailIndustryPageKey } from './retailIndustryLabels.js';
import { createRetailIndustryPages } from './retailIndustryPages.js';
import type { LandingLocale, LandingTeamMemberContent } from './landingContent.js';
import type { StaticPageAudience, StaticPageContent } from './staticPageContent.js';
import { audiencePages } from './staticPageAudienceLocalization.js';
import { createApplicationPage } from './applicationPageContent.js';
import { commonPages } from './staticPageCommonLocalization.js';
import { createNewIndustryPages, industryGalleryDefaults, industryOverviewCopy } from './industryPageContent.js';
import { isIndustryPageKey, newIndustryPageKeys } from './industryPages.js';

const mergeLocalizedCopy = (source: unknown, translated: unknown): unknown => {
  if (!translated) return source;
  if (Array.isArray(source)) {
    if (!Array.isArray(translated)) return source;
    return source.map((item, index) => mergeLocalizedCopy(item, translated[index]));
  }
  if (!source || typeof source !== 'object' || !translated || typeof translated !== 'object') {
    return translated ?? source;
  }

  const result = { ...source } as Record<string, unknown>;
  for (const [key, value] of Object.entries(translated)) {
    if (value === undefined) continue;
    result[key] = mergeLocalizedCopy(result[key], value);
  }
  return result;
};

export const localizeStaticPage = (
  page: StaticPageContent,
  audience: StaticPageAudience,
  locale: LandingLocale,
  localizedTeam: LandingTeamMemberContent[],
): StaticPageContent => {
  if (isCareIndustryPageKey(page.key)) return createCareIndustryPages(locale)[page.key];
  if (isRetailIndustryPageKey(page.key)) return createRetailIndustryPages(locale)[page.key];
  if (page.key === 'bewerben') return createApplicationPage(locale);
  if (newIndustryPageKeys.some(key => key === page.key)) {
    const translated = createNewIndustryPages(locale);
    return translated[page.key as keyof typeof translated];
  }
  if (isIndustryPageKey(page.key)) page = { ...page, ...industryOverviewCopy(locale), heroGallery: industryGalleryDefaults(page.key, locale) };
  if (locale === 'de') return page;

  const commonCopy = commonPages[locale][page.key];
  const audienceCopy = audiencePages[locale][audience][page.key];
  let localizedPage = mergeLocalizedCopy(page, commonCopy) as StaticPageContent;
  localizedPage = mergeLocalizedCopy(localizedPage, audienceCopy) as StaticPageContent;

  if (page.key === 'team') localizedPage = { ...localizedPage, team: localizedTeam.map(member => ({ ...member })) };
  return localizedPage;
};

export const staticPageUiCopy = (locale: LandingLocale) => {
  if (locale === 'tr') {
    return {
      teamPreview: 'Ekip önizlemesi',
      teamSectionEyebrow: 'Viva Porta’nın arkasındaki insanlar',
      teamSectionTitle: 'Net sonraki adımlar için doğrudan irtibat kişileri.',
      teamSectionText: 'Adaylara ve işverenlere günlük süreçte eşlik eden insanları tanı.',
      teamGroups: ['Yönetim ve operasyon', 'Danışma kurulu', 'Ekip'],
      linkedInProfile: (name: string) => `${name} LinkedIn profili`,
      story: 'Hikâye',
      motivation: 'Motivasyon',
      applicationForm: 'Başvuru formuna git',
      processEyebrow: 'Adım adım',
      processTitle: 'Kişisel destek sunan net bir süreç.',
      processStages: (count: number) => `${count} net aşama`,
      personallyGuided: 'Kişisel destek',
      untilFirstDay: 'İlk iş gününe kadar',
      faqEyebrow: 'SSS',
      faqTitle: 'Sektör hakkında sık sorulan sorular.',
      globusJobSearch: {
        label: 'İş ilanlarında ara',
        placeholder: 'Meslek, görev veya deneyim ara',
        filtersLabel: 'İş ilanına göre filtrele',
        allJobs: 'Tüm işler',
        clearSearch: 'Aramayı temizle',
        resetFilters: 'Tüm işleri göster',
        results: (count: number, total: number) => `${total} iş ilanından ${count} tanesi gösteriliyor`,
        emptyTitle: 'Uygun iş ilanı bulunamadı',
        emptyText: 'Arama terimini değiştir veya tüm iş ilanlarını tekrar göster.',
      },
    };
  }
  if (locale === 'en') {
    return {
      teamPreview: 'Team preview',
      teamSectionEyebrow: 'People behind Viva Porta',
      teamSectionTitle: 'Direct contacts for clear next steps.',
      teamSectionText: 'Meet the people who guide applicants and employers through the daily process.',
      teamGroups: ['Management and operations', 'Advisory board', 'Team'],
      linkedInProfile: (name: string) => `${name}'s LinkedIn profile`,
      story: 'Story',
      motivation: 'Motivation',
      applicationForm: 'Go to application form',
      processEyebrow: 'Step by step',
      processTitle: 'A clear process with personal guidance.',
      processStages: (count: number) => `${count} clear stages`,
      personallyGuided: 'Personally guided',
      untilFirstDay: 'Until the first working day',
      faqEyebrow: 'FAQ',
      faqTitle: 'Frequently asked questions about the industry.',
      globusJobSearch: {
        label: 'Search job openings',
        placeholder: 'Search by role, task or experience',
        filtersLabel: 'Filter by job opening',
        allJobs: 'All jobs',
        clearSearch: 'Clear search',
        resetFilters: 'Show all jobs',
        results: (count: number, total: number) => `Showing ${count} of ${total} job openings`,
        emptyTitle: 'No matching job opening found',
        emptyText: 'Try a different search term or show all job openings again.',
      },
    };
  }
  return {
    teamPreview: 'Teamvorschau',
    teamSectionEyebrow: 'Menschen hinter Viva Porta',
    teamSectionTitle: 'Direkte Ansprechpartner für klare nächste Schritte.',
    teamSectionText: 'Lerne die Menschen kennen, die Kandidaten und Arbeitgeber im Alltag begleiten.',
    teamGroups: ['Geschäftsführung und operative Leitung', 'Beiräte', 'Das Team'],
    linkedInProfile: (name: string) => `LinkedIn-Profil von ${name}`,
    story: 'Story',
    motivation: 'Motivation',
    applicationForm: 'Zum Anmeldeformular',
    processEyebrow: 'Schritt für Schritt',
    processTitle: 'Ein klarer Prozess mit persönlicher Begleitung.',
    processStages: (count: number) => `${count} klare Etappen`,
    personallyGuided: 'Persönlich begleitet',
    untilFirstDay: 'Bis zum ersten Arbeitstag',
    faqEyebrow: 'FAQ',
    faqTitle: 'Häufige Fragen zur Branche.',
    globusJobSearch: {
      label: 'Stellenausschreibungen durchsuchen',
      placeholder: 'Nach Beruf, Aufgabe oder Erfahrung suchen',
      filtersLabel: 'Nach Stellenangebot filtern',
      allJobs: 'Alle Stellen',
      clearSearch: 'Suche löschen',
      resetFilters: 'Alle Stellen anzeigen',
      results: (count: number, total: number) => `${count} von ${total} Stellenausschreibungen`,
      emptyTitle: 'Keine passende Stelle gefunden',
      emptyText: 'Passe deine Suche an oder zeige wieder alle Stellenausschreibungen.',
    },
  };
};
